import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Images } from 'lucide-react';
import { galeriaAdminService } from '../../api/services';
import { Button, Modal, Select, Loading, Vazio, Badge } from '../../components/ui';
import { PAGINAS_GALERIA } from '../../types';
import type { Imagem, PaginaGaleria } from '../../types';
import toast from 'react-hot-toast';
import './admin-paginas.css';

export default function AdminGaleriaPage() {
  const [imagens, setImagens] = useState<Imagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await galeriaAdminService.listar(filtro || undefined);
      setImagens(r.imagens);
    } catch { toast.error('Erro ao carregar galeria'); }
    finally { setCarregando(false); }
  }, [filtro]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDeletar = async (img: Imagem) => {
    if (!window.confirm(`Remover "${img.titulo}"?`)) return;
    try {
      await galeriaAdminService.deletar(img.id);
      toast.success('Imagem removida');
      carregar();
    } catch { toast.error('Erro ao remover'); }
  };

  const labelPagina = (p: string) => PAGINAS_GALERIA.find(x => x.value === p)?.label || p;

  return (
    <div className="admin-pagina">
      <div className="admin-pagina-header">
        <div>
          <h1>Galeria de Imagens</h1>
          <p>Fotos de eventos, festejo, capa do site e logo da LICMAB</p>
        </div>
        <Button icone={<Plus size={16} />} onClick={() => setModalAberto(true)}>
          Adicionar Imagem
        </Button>
      </div>

      {/* Filtro por página */}
      <div className="admin-filtros">
        <Select
          opcoes={PAGINAS_GALERIA}
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
          placeholder="Todas as seções"
        />
      </div>

      {carregando ? <Loading /> : imagens.length === 0 ? (
        <Vazio icone={<Images size={48} />} titulo="Nenhuma imagem" descricao="Clique em 'Adicionar Imagem' para começar." />
      ) : (
        <div className="galeria-admin-grid">
          {imagens.map(img => (
            <div key={img.id} className="galeria-admin-card">
              <div className="galeria-admin-img">
                <img src={img.urlPublica} alt={img.titulo} loading="lazy" />
                <button
                  className="galeria-admin-del"
                  onClick={() => handleDeletar(img)}
                  title="Remover imagem"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="galeria-admin-info">
                <p className="galeria-admin-titulo">{img.titulo}</p>
                <Badge texto={labelPagina(img.pagina)} cor="azul" />
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal aberto={modalAberto} onFechar={() => setModalAberto(false)} titulo="Adicionar Imagem" largura="md">
        <FormImagem onSalvar={() => { setModalAberto(false); carregar(); }} />
      </Modal>
    </div>
  );
}

function FormImagem({ onSalvar }: { onSalvar: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [pagina, setPagina] = useState<PaginaGaleria>('galeria');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const handleArquivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setArquivo(f);
      const url = URL.createObjectURL(f);
      setPreview(url);
    }
  };

  const handleSalvar = async () => {
    if (!arquivo) { toast.error('Selecione uma imagem'); return; }
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append('titulo', titulo || arquivo.name);
      fd.append('pagina', pagina);
      fd.append('arquivo', arquivo);
      await galeriaAdminService.upload(fd);
      toast.success('Imagem publicada!');
      onSalvar();
    } catch (e: any) {
      toast.error(e?.response?.data?.erro || 'Erro ao enviar');
    } finally { setSalvando(false); }
  };

  return (
    <div className="form-stack">
      <Select
        label="Seção do site"
        opcoes={PAGINAS_GALERIA}
        value={pagina}
        onChange={e => setPagina(e.target.value as PaginaGaleria)}
      />
      <div className="campo-grupo">
        <label className="campo-label">Título (opcional)</label>
        <input
          type="text"
          className="campo-input"
          value={titulo}
          onChange={e => setTitulo(e.target.value)}
          placeholder="Descrição da imagem..."
        />
      </div>
      <div className="campo-grupo">
        <label className="campo-label">Imagem (JPG, PNG, WebP)</label>
        <input
          type="file"
          className="campo-input"
          accept="image/*"
          onChange={handleArquivo}
        />
      </div>
      {preview && (
        <div className="form-preview">
          <img src={preview} alt="preview" />
        </div>
      )}
      <div className="form-acoes">
        <Button variante="primario" carregando={salvando} onClick={handleSalvar}>
          Publicar Imagem
        </Button>
      </div>
    </div>
  );
}
